<template>
  <DefaultModal class="preview-modal" @close="$emit('close')">
    <template #body>
      <div v-if="isLoading" class="spinner-wrapper">
        <div class="spinner-container">
          <div class="spinner" />
          <p class="spinner-text">Laddar förhandsgranskning...</p>
        </div>
      </div>

      <img
        v-if="!isPdf"
        class="image"
        :src="previewUrl"
        alt="Förhandsgranskning"
        @load="onLoad"
        @error="onError"
      />

      <div v-else class="pdf-wrap">
        <iframe
          :src="previewUrl"
          title="PDF-förhandsgranskning"
          @load="onLoad"
        />
        <p class="fallback">
          Om PDF:en inte syns:
          <a :href="previewUrl" target="_blank" rel="noopener"
            >Öppna i ny flik</a
          >
        </p>
      </div>

      <div v-if="hasError" class="error">
        <p>Kunde inte ladda förhandsgranskningen.</p>
        <a :href="previewUrl" target="_blank" rel="noopener">Öppna i ny flik</a>
      </div>
    </template>

    <template #footer>
      <BaseButton
        :is-rounded="true"
        :icon="svgs.ICONS.DOWNLOAD"
        :href="fileUrl"
        download
      >
        Ladda ner
      </BaseButton>
      <BaseButton
        :is-rounded="true"
        :href="previewUrl"
        :icon="svgs.ICONS.EXTERNAL_LINK"
        target="_blank"
        rel="noopener"
        class="btn"
        >Öppna i ny flik</BaseButton
      >
      <BaseButton :is-rounded="true" :is-default="true" @click="$emit('close')"
        >Stäng</BaseButton
      >
    </template>
  </DefaultModal>
</template>

<script>
import svgs from '@/utils/icon-constants';

import DefaultModal from '@/components/DefaultModal.vue';
import BaseButton from '@/components/BaseButton.vue';

export default {
  name: 'FilePreviewModal',
  components: {
    DefaultModal,
    BaseButton
  },
  props: {
    mime: {
      type: String,
      default: ''
    },
    url: {
      type: String,
      required: true
    },
    isMerged: {
      type: Boolean,
      default: false
    },
    isAttachment: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close'],

  data() {
    return {
      isLoading: true,
      hasError: false,
      timeoutId: null,
      svgs
    };
  },
  computed: {
    isPdf() {
      return this.mime.includes('pdf');
    },
    fileUrl() {
      return this.url;
    },
    previewUrl() {
      const url = new URL(this.url);
      url.searchParams.set('disposition', 'inline');
      if (this.isMerged) {
        url.searchParams.set('source', 'merged');
      } else if (this.isAttachment) {
        url.searchParams.set('source', 'attachment');
      }
      return url.toString();
    }
  },
  watch: {
    previewUrl: {
      immediate: true,
      handler() {
        this.isLoading = true;
        this.hasError = false;
        clearTimeout(this.timeoutId);
        this.timeoutId = setTimeout(() => {
          this.isLoading = false;
        }, 10000);
      }
    }
  },
  beforeUnmount() {
    clearTimeout(this.timeoutId);
  },
  methods: {
    onLoad() {
      this.isLoading = false;
      this.hasError = false;
      clearTimeout(this.timeoutId);
    },
    onError() {
      this.isLoading = false;
      this.hasError = true;
      clearTimeout(this.timeoutId);
    }
  }
};
</script>

<style scoped>
.spinner-wrapper {
  height: 75vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.spinner-container {
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.6);
  z-index: 2;
  text-align: center;
  padding: 1.5rem;
}

.spinner {
  width: 4.2rem;
  height: 4.2rem;
  border: 4px solid #e6e6e6;
  border-top-color: var(--color-blue);
  border-radius: 50%;
  animation: spin-43d4a311 0.9s linear infinite;
  margin-bottom: 1rem;
}

.spinner-text {
  font-size: 1.4rem;
  color: var(--color-dark-blue);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border-radius: 12px;
  box-shadow: 0px 2px 15px rgba(0, 0, 0, 0.05);
  margin: 0 auto;
  display: block;
}
.pdf-wrap {
  height: 70vh;
  width: 800px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0px 2px 15px rgba(0, 0, 0, 0.05);
}
.pdf-wrap iframe {
  width: 100%;
  height: 100%;
  border: none;
}
.fallback {
  padding: 8px 12px;
}
.error {
  padding: 12px;
  color: #b00020;
}

.preview-modal :deep(.footer) {
  padding: 3.5rem;
}
</style>
